import { _ as _extends } from '../../_rollupPluginBabelHelpers-c245b26a.js';
import React, { forwardRef, useContext, useRef, useEffect } from 'react';
import { useAutoPosition } from '../../hooks/useAutoPosition.js';
import { DropdownContext } from './Dropdown.js';
import { DropdownPopover } from './DropdownPopover.js';
import { jsx } from '@emotion/react';
import { MenuOverlay } from '../Menu/MenuOverlay.js';
import { MenuLabel } from '../Menu/MenuLabel.js';
import { AccessibleTrap } from '../AccessibleTrap/AccessibleTrap.js';
import { StyledMenuItems } from '../Menu/MenuItems.js';
import { Box } from '../Box/Box.js';
import '../../reach-popover.esm-c19739dd.js';
import 'react-dom';
import '../../hooks/useWindowSize.js';
import '@indeed/downshift';
import '../../utils/isFunction.js';
import 'react-fast-compare';
import '../../constants/index.js';
import '@indeed/ihl-use-id';
import '../../utils/warnDeprecation.js';
import '../../utils/warn.js';
import '@indeed/ifl-css';
import '../../utils/mergeRefs.js';
import '@indeed/ifl-icons';
import '../Heading/Heading.js';
import '../../utils/createResponsive.js';
import '../Text/Text.js';
import '../IconWrapper/IconWrapper.js';
import 'react-remove-scroll';
import '../AccessibleTrap/FocusTrap.js';
import 'react-focus-lock';
import '../../hooks/useInertA11yBackground.js';
import '../../hooks/useForceUpdate.js';
import '../../reach-menu-button.esm-22bcdd5f.js';
import '../Menu/MenuItem.js';
import 'focus-visible';
import '../../utils/styled.js';

/**
 * @deprecated The `Dropdown` component will be removed in IFL 7.
 * See the IFL 7 preparation guide for more information: https://link.indeed.tech/ifl-7-prep-guide
 */

const DropdownMenuList = /*#__PURE__*/forwardRef((_ref, ref) => {
  let {
    children,
    label,
    labelProps = {},
    mobile = "auto",
    position,
    sx,
    ...props
  } = _ref;
  const {
    isOpen,
    targetRef,
    closeMenu,
    getMenuProps,
    setIsMobile,
    multiple
  } = useContext(DropdownContext);
  const [isMobile, positionFn] = useAutoPosition({
    mobile,
    position
  });
  const menuWidth = targetRef && targetRef.current && targetRef.current.clientWidth;
  const labelRef = useRef(null);
  useEffect(() => {
    setIsMobile(isMobile);
  }, [isMobile, setIsMobile]);
  const handleKeyDown = e => {
    var _labelRef$current;
    /**
     * Hook into Downshift's default onKeyDown event.
     *
     * Downshift assumes the menu should be closed when tabbing out of the
     * element/component. We need to prevent this behavior on "mobile" since
     * the list of options display in a "modal" that includes an additional
     * interactive element (the close button).
     */
    const closeButton = (_labelRef$current = labelRef.current) == null ? void 0 : _labelRef$current.querySelector("button");
    if (e.key === "Tab") {
      e.preventDefault();
      closeButton == null || closeButton.focus();
    }
  };
  const handleOnBlur = e => {
    /**
     * Hook into Downshift's default onBlur event.
     *
     * Downshift assumes the menu should be closed when blurring out of the
     * menu. Instead, we need focus to be sent to the close button in the
     * "mobile" modal. See above handleKeyDown method.
     */

    // @ts-ignore
    e.nativeEvent.preventDownshiftDefault = true;
  };
  const onCloseMenu = () => {
    var _targetRef$current;
    const {
      onClose
    } = labelProps;
    closeMenu();
    if (onClose) {
      onClose();
    }
    (_targetRef$current = targetRef.current) == null || _targetRef$current.focus();
  };
  return jsx(DropdownPopover, {
    position: positionFn,
    sx: sx
  }, isMobile ? jsx(React.Fragment, null, jsx(MenuOverlay, {
    onClick: onCloseMenu
  }), jsx(AccessibleTrap, {
    disabled: !isOpen
  }, jsx(StyledMenuItems, _extends({
    ref: ref,
    sx: {
      minInlineSize: menuWidth
    },
    isMobile: true
  }, props), jsx(MenuLabel, _extends({
    ref: labelRef
  }, labelProps, {
    onClose: onCloseMenu
  }), label), jsx(Box, _extends({
    sx: {
      flex: "1 1 auto",
      overflow: "auto",
      zIndex: 1,
      ":focus": {
        outline: "none"
      }
    }
  }, getMenuProps({
    "aria-multiselectable": multiple ? true : undefined,
    onKeyDown: handleKeyDown,
    onBlur: handleOnBlur
  }, {
    suppressRefError: true
  }), {
    // Keep menu list in tab order. Othwerise, focus trapping doesn't work.
    tabIndex: 0
  }), children)))) : jsx(StyledMenuItems, _extends({
    ref: ref,
    sx: {
      minInlineSize: menuWidth
    },
    isMobile: false
  }, props), jsx(Box, _extends({
    sx: {
      flex: "1 1 auto",
      overflow: "auto",
      zIndex: 1,
      ":focus": {
        outline: "none"
      }
    }
  }, getMenuProps({
    "aria-multiselectable": multiple ? true : undefined
  }, {
    suppressRefError: true
  })), children)));
});
DropdownMenuList.displayName = "DropdownMenuList";

export { DropdownMenuList };
